import { Link } from "react-router-dom";
import { ArrowRight, Battery, ShieldCheck, ExternalLink, Compass } from "lucide-react";

const TAG = "eigoo0fa-20";
const BLUETTI_URL = `https://www.amazon.com/dp/B0B2WP6S9V/?tag=${TAG}`;
const PROTON_URL = "https://protonvpn.com/?ref=eigoo";

function ArmorBlock({ icon: Icon, label, name, copy, points, url, accent, slug }) {
    const ring = accent === "cyan" ? "border-cyan-sov" : "border-gold";
    const txt = accent === "cyan" ? "text-cyan-sov" : "text-gold";
    return (
        <article
            className={`signal-card relative bg-forest-deep rounded-sm p-7 md:p-10`}
            data-testid={`armor-${slug}`}
        >
            <div className="flex items-start gap-5">
                <div
                    className={`shrink-0 w-14 h-14 rounded-full bg-cream-warm border ${ring} flex items-center justify-center ${txt}`}
                >
                    <Icon size={22} aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                    <div className={`text-[0.6rem] tracking-[0.32em] uppercase ${txt} mb-2`}>
                        {label}
                    </div>
                    <h2 className="font-serif text-3xl sm:text-4xl text-cream leading-[1.05]">
                        {name}
                    </h2>
                    <p className="mt-4 text-cream/80 leading-relaxed max-w-2xl">
                        {copy}
                    </p>
                    <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-cream/75">
                        {points.map((p) => (
                            <li
                                key={p}
                                className="flex items-start gap-2 leading-relaxed"
                            >
                                <span className={`mt-1.5 inline-block w-1.5 h-1.5 rounded-full ${accent === "cyan" ? "bg-cyan-sov" : "bg-gold"}`} />
                                {p}
                            </li>
                        ))}
                    </ul>
                    <a
                        href={url}
                        target="_blank"
                        rel="sponsored noopener noreferrer"
                        className="btn-pill btn-primary mt-7"
                        data-testid={`armor-cta-${slug}`}
                    >
                        Shop the {name.split("·")[0].trim()}
                        <ExternalLink size={14} />
                    </a>
                </div>
            </div>
        </article>
    );
}

export default function SurvivalBrief() {
    return (
        <div className="bg-cream min-h-screen relative" data-testid="survival-brief-page">
            <div className="absolute inset-0 starfield opacity-[0.55] pointer-events-none" />

            {/* Hero */}
            <section className="relative max-w-5xl mx-auto px-5 md:px-10 pt-20 md:pt-28 pb-14">
                <span className="divider-rule text-brown">
                    <Compass size={12} /> Sovereign Survival Brief · Vol. I
                </span>
                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] leading-[0.96] text-gold mt-6 text-glow-gold">
                    Essential
                    <br />
                    <span className="italic text-cyan-sov text-glow-cyan">
                        Hawaii Armor.
                    </span>
                </h1>
                <p className="mt-8 max-w-2xl text-cream/80 leading-relaxed text-lg">
                    The trade winds carry beauty and chaos in equal measure.
                    Hurricane corridors. Lava-line outages. State-level traffic
                    inspection. The Sovereign Operator does not negotiate with
                    weather or wire — they
                    <span className="text-gold font-semibold"> armor up</span>.
                </p>
                <p className="mt-5 max-w-2xl text-cream/70 leading-relaxed">
                    Two signals dominate every island operator's go-bag. One
                    keeps the lights on when the grid blinks; the other keeps
                    the transmission opaque when the wire is being read. Pair
                    them. Charge them. Cloak them.
                </p>
            </section>

            {/* Armor Block 1 · Bluetti */}
            <section className="relative max-w-5xl mx-auto px-5 md:px-10 pb-10">
                <ArmorBlock
                    slug="bluetti"
                    icon={Battery}
                    label="Armor I · Power Sovereignty"
                    name="Bluetti AC200MAX · Off-Grid Vault"
                    copy="When Mauna Loa coughs or a Category 4 takes the trunk lines down, you don't want to be discovering your headlamp's dead. The AC200MAX is a 2 048 Wh LFP vault — silent, solar-ingestible, hurricane-grade. Modular up to 8 192 Wh. The Sovereign's electric heartbeat."
                    points={[
                        "2 200 W pure-sine inverter (4 800 W surge)",
                        "2 048 Wh LiFePO4 · 3500+ cycle life",
                        "900 W solar ingest · MPPT controller",
                        "16 outlets · USB-C PD 100 W · car ports",
                        "Wi-Fi + Bluetooth · app-orchestrated",
                        "Stackable to 8 192 Wh with B230/B300",
                    ]}
                    url={BLUETTI_URL}
                    accent="gold"
                />
            </section>

            {/* Armor Block 2 · ProtonVPN */}
            <section className="relative max-w-5xl mx-auto px-5 md:px-10 pb-10">
                <ArmorBlock
                    slug="proton"
                    icon={ShieldCheck}
                    label="Armor II · Signal Cloak"
                    name="ProtonVPN · Encrypted Mesh"
                    copy="The wire is being read. Honolulu IXP routes through known interception points; consumer ISPs sell session telemetry by the kilobyte. ProtonVPN is the Swiss-jurisdiction, no-logs, mathematically-audited cloak the EIGOO operator threads every transmission through. Secure Core multi-hop. NetShield ad/tracker/malware fence. Tor-routed exit. Sovereign by court order, not by marketing slogan."
                    points={[
                        "Swiss jurisdiction · no-logs · independent audits",
                        "Secure Core multi-hop · double-encrypted exit",
                        "NetShield · DNS-level tracker/ad/malware fence",
                        "Tor over VPN · onion routing through .ch nodes",
                        "Open-source clients · transparent codebase",
                        "P2P-grade Plus tier · 10 simultaneous devices",
                    ]}
                    url={PROTON_URL}
                    accent="cyan"
                />
            </section>

            {/* Closing rune */}
            <section className="relative max-w-3xl mx-auto px-5 md:px-10 pt-6 pb-24">
                <div className="p-8 bg-cream-warm border border-gold/20 rounded-sm relative overflow-hidden">
                    <div className="grain absolute inset-0" />
                    <p className="relative text-cream/80 leading-relaxed italic font-serif text-xl text-glow-gold">
                        “The Sovereign does not pray for fair weather. The
                        Sovereign owns the lantern, the line, and the cloak —
                        and walks calmly through the storm.”
                    </p>
                    <p className="relative mt-4 text-[0.6rem] tracking-[0.32em] uppercase text-brown">
                        — EIGOO Field Manual · Mililani Wharf
                    </p>
                </div>
                <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                    <Link
                        to="/inventory"
                        className="btn-pill btn-ghost"
                        data-testid="brief-back-to-inventory"
                    >
                        <ArrowRight size={14} /> See the full Inventory
                    </Link>
                    <p className="text-[0.6rem] tracking-[0.28em] uppercase text-brown/70">
                        Amazon Associates · eigoo0fa-20 · Affiliate disclosed
                    </p>
                </div>
            </section>
        </div>
    );
}
